import {
  DEBOUNCE_TIME,
  handleSelectionChangeOnPaginatedData,
} from '@issuemanager/apps/client/utils';
import {
  type AutocompleteInputChangeReason,
  debounce,
  useEventCallback,
} from '@mui/material';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import React, { useMemo } from 'react';
import type { ExtendedAutocompleteProps } from '../extended-autocomplete/types';
import { BaseElement, isOptionEqualToValue } from './utils';

export interface UseMultipleSelectorProps<T extends BaseElement> {
  setInputValue?: (value: string) => void;
  // TODO: Remove `query`prop and replace with `loading` and `onScrollEnd` props
  query?: UseInfiniteQueryResult;
  loading?: boolean;
  onScrollEnd?: () => void;
  value?: T[];
  options: T[];
  setValue?: (value: T[]) => void;
  partialValue: T[];
  setPartialValue: (value: T[]) => void;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
}

export interface UseMultipleSelectorReturnValue<T extends BaseElement>
  extends Pick<
    ExtendedAutocompleteProps<T, true, true, false>,
    | 'multiple'
    | 'onInputChange'
    | 'onScrollEnd'
    | 'disableCloseOnSelect'
    | 'loading'
    | 'ApplyButtonProps'
    | 'ClearButtonProps'
    | 'value'
    | 'isOptionEqualToValue'
    | 'onClear'
    | 'onApply'
    | 'onChange'
    | 'onOpen'
  > {
  isOptionEqualToValue: (option: T, value: T) => boolean;
}

export function useMultipleSelectorProps<T extends BaseElement>(
  props: UseMultipleSelectorProps<T>,
): UseMultipleSelectorReturnValue<T> {
  const {
    options,
    setInputValue,
    query,
    loading,
    onScrollEnd,
    value = [],
    setValue,
    partialValue,
    setPartialValue,
    isOptionEqualToValue: isOptionEqualToValueProp,
  } = props;
  const debouncedInputChange = useMemo(
    () =>
      debounce((_, value) => {
        setInputValue?.(value);
      }, DEBOUNCE_TIME),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleInputChange = useEventCallback(
    (
      event: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === 'reset') return;
      debouncedInputChange(event, value);
    },
  );

  const handleScrollEnd = useEventCallback(
    debounce(() => {
      query?.fetchNextPage();
      onScrollEnd?.();
    }, DEBOUNCE_TIME),
  );

  const handleClear = useEventCallback(() => {
    setPartialValue([]);
  });

  const handleApply = useEventCallback(() => {
    setValue?.(partialValue);
  });

  const handleOpen = useEventCallback(() => {
    setPartialValue(value);
  });

  const handleChange = useEventCallback(
    (e: React.SyntheticEvent, selected: T[]) => {
      // If it's a keyboard event and coming from an input, we need to check the value
      // to prevent unwanted deselection when using delete/backspace
      if (e.type === 'keydown' && e.target instanceof HTMLInputElement) {
        if (!e.target.value) return;
      }

      const finalSelection = handleSelectionChangeOnPaginatedData(
        partialValue,
        selected,
        options,
        isOptionEqualToValueProp ?? isOptionEqualToValue,
      );
      setPartialValue(finalSelection);
    },
  );

  return {
    multiple: true,
    disableCloseOnSelect: true,
    onInputChange: handleInputChange,
    onScrollEnd: handleScrollEnd,
    loading: loading ?? query?.isLoading,
    value: partialValue,
    isOptionEqualToValue: isOptionEqualToValueProp ?? isOptionEqualToValue,
    onClear: handleClear,
    onApply: handleApply,
    onOpen: handleOpen,
    onChange: handleChange,
    ApplyButtonProps: {
      disabled: isEqual(value, partialValue),
    },
    ClearButtonProps: {
      disabled: !partialValue.length,
    },
  };
}
